// Leaf module (no imports) so a store, a grid and the report path can share the rule without a cycle.

// A disabled field is gone whatever anyone saved; defaultHidden answers where the model is silent or not boolean.
export const isFieldVisible = (
  field: string,
  defaultHidden: boolean | undefined,
  disabledFields: readonly string[] | undefined,
  columnVisibilityModel: Record<string, boolean> | undefined
): boolean => {
  if (disabledFields?.includes(field)) return false;
  const saved: unknown = columnVisibilityModel?.[field];
  if (typeof saved === 'boolean') return saved;

  return !defaultHidden;
};

// An off-by-default column a model omits stays hidden: saved before it existed, never saved, or joined after a restore.
export const applyDefaultHiddenColumns = (
  columnVisibilityModel: Record<string, boolean>,
  columns: readonly { field: string; defaultHidden?: boolean }[]
): string[] => {
  const filled: string[] = [];
  columns.forEach((col) => {
    if (col.defaultHidden && !(col.field in columnVisibilityModel)) {
      columnVisibilityModel[col.field] = false;
      filled.push(col.field);
    }
  });
  return filled;
};

// The user's own visibility: what they saved (including columns an account's later change decided here), plus
// what they switched, which includes what a save not yet landed named.
export const ownColumnVisibility = (
  current: Record<string, boolean>,
  laidOut: Record<string, boolean>,
  filledByGrid: ReadonlySet<string>,
  overridden: Readonly<Record<string, boolean>> = {},
  pending: ReadonlySet<string> = new Set()
): { columnVisibilityModel: Record<string, boolean>; switched: string[] } => {
  const switched = [...new Set([...Object.keys(current), ...Object.keys(laidOut), ...pending])].filter(
    (field) => pending.has(field) || (current[field] ?? true) !== (laidOut[field] ?? true)
  );
  const columnVisibilityModel = Object.fromEntries(
    Object.entries(current).filter(([field]) => !filledByGrid.has(field) || switched.includes(field))
  );
  Object.entries(overridden).forEach(([field, visible]) => {
    if (!switched.includes(field)) columnVisibilityModel[field] = visible;
  });
  // A column shown by deleting its key would otherwise come back hidden by the default the grid fills in.
  switched.forEach((field) => {
    if (!(field in current)) columnVisibilityModel[field] = true;
  });
  return { columnVisibilityModel, switched };
};
